import { Input } from "@components/ui/input";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TextEditor, { RichTextEditor } from "@ui/external/components/texteditor";
import { Equal, XIcon } from "lucide-react";
import React from "react";
import { Control, Controller } from "react-hook-form";
import { DndUpload } from "../formelement";

type ItemType = {
  type: "description" | "quotation" | "image";
  id: number;
  question?: string;
  answer?: string;
  description?: string;
  label?: string;
  image?: File | string | "";
};

interface DragChildProps {
  field: ItemType;
  control: Control<any>; // or your exact Form type
  id: number; // index in the items array
  isDragActive: boolean;
  remove: (index: number) => void; // function to remove the item
}

const DragChild: React.FC<DragChildProps> = ({
  field,
  control,
  remove,
  id,
  isDragActive = false,
}) => {
  let type = field?.type;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: field?.id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!type) {
    if (field.hasOwnProperty("question") && field.hasOwnProperty("answer")) {
      type = "description";
    } else if (
      field?.hasOwnProperty("label") &&
      field?.hasOwnProperty("image")
    ) {
      type = "image";
    } else if (field?.hasOwnProperty("description")) {
      type = "quotation";
    }
  }
  switch (type) {
    case "description":
      return (
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          className={`w-full p-5 rounded-md my-4 bg-white h-fit border`}
        >
          <div className="flex my-3 justify-between gap-3 text-[#212121] font-bold">
            <div className="flex items-center  w-full gap-2">
              {" "}
              <Equal {...listeners} className="cursor-grab" /> Description
            </div>
            <XIcon
              className="h-5 w-5 cursor-pointer"
              onClick={() => {
                remove(id);
              }}
            />
          </div>
          {!isDragActive && (
            <>
              <Controller
                control={control}
                name={`items.${id}.question`}
                render={({ field }) => (
                  <div>
                    <span className="block my-2 text-[#212121] font-semibold">
                      Question
                    </span>
                    <Input {...field} />
                  </div>
                )}
              />

              <Controller
                control={control}
                name={`items.${id}.answer`}
                render={({ field }) => (
                  <div>
                    <span className="block my-2 text-[#212121] font-semibold">
                      Answer
                    </span>
                    <div className="">
                      {" "}
                      <TextEditor
                        {...field}
                        initialValue={field.value}
                        onChange={field.onChange}
                      />
                    </div>
                  </div>
                )}
              />
            </>
          )}
        </div>
      );

    case "quotation":
      return (
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          className="w-full p-5 rounded-md  my-4 border bg-white"
        >
          <div className="flex my-3 justify-between gap-3 text-[#212121] font-bold">
            <div className="flex items-center  w-full gap-2">
              {" "}
              <Equal {...listeners} className="cursor-grab" /> Quotation
            </div>
            <XIcon
              className="h-5 w-5 cursor-pointer"
              onClick={() => {
                remove(id);
              }}
            />
          </div>
          {!isDragActive && (
            <Controller
              control={control}
              name={`items.${id}.description`}
              render={({ field }) => (
                <div>
                  <span className="block my-2 text-[#212121] font-semibold">
                    Answer
                  </span>
                  <div className="">
                    {" "}
                    <TextEditor
                      {...field}
                      initialValue={field.value}
                      onChange={field.onChange}
                    />
                  </div>
                </div>
              )}
            />
          )}
        </div>
      );

    case "image":
      return (
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          className="w-full p-5   my-4 rounded-md border bg-white"
        >
          <div className="flex my-3 justify-between gap-3 text-[#212121] font-bold">
            <div className="flex items-center  w-full gap-2">
              {" "}
              <Equal {...listeners} className="cursor-grab" /> Image
            </div>
            <XIcon
              className="h-5 w-5 cursor-pointer"
              onClick={() => {
                remove(id);
              }}
            />
          </div>

          {!isDragActive && (
            <>
              <Controller
                control={control}
                name={`items.${id}.label`}
                render={({ field }) => (
                  <div>
                    <span className="block my-2 text-[#212121] font-semibold">
                      Label
                    </span>
                    <Input {...field} />
                  </div>
                )}
              />

              <Controller
                control={control}
                name={`items.${id}.image`}
                render={({ field: { value, onChange, ...restFields } }) => (
                  <div>
                    <span className="block my-2 text-[#212121] font-semibold">
                      Image
                    </span>

                    <DndUpload
                      {...restFields}
                      value={value}
                      onChange={onChange}
                      cropperHeight={200}
                      cropperWidth={200}
                      cropperWidthPlaceholder={384}
                      cropperHeightPlaceholder={451}
                      type="image"
                    />
                  </div>
                )}
              />
            </>
          )}
        </div>
      );

    default:
      return (
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          {...listeners}
          className="w-full"
        >
          Default
        </div>
      );
  }
};

export default DragChild;
